import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import LayoutNoLogin from '../../components/layout/LayoutNoLogin';
import { motion } from 'framer-motion';
import { Disclosure, DisclosureButton, DisclosurePanel } from '@headlessui/react';
import DemoQuiz from '../../components/quiz/DemoQuiz';
import LearningSteps from '../../components/landing/LearningSteps';
import FAQSection from '../../components/landing/FAQSection';
import {
  BookOpen,
  Brain,
  Clock,
  ChevronRight,
  CheckCircle,
  AlertTriangle,
  BookOpenCheck,
  GraduationCap,
  Maximize2,
  Minimize2,
  BrainCircuit,
  BarChart3,
  Target
} from 'lucide-react';
import { getNumberOfUsers } from '../../services/userAPIService';

// Images
import heroImage from '../../assets/heropicture.jpg';
import View_Topic from '../../assets/View_Topic.png';
import View_HomeScreen from '../../assets/View_HomeScreen.png';
import View_Quiz from '../../assets/View_Quiz.png';
import View_Wiki from '../../assets/View_WIKI.png';
import View_Exam from '../../assets/View_Exam.png';


const LandingPage = () => {

  const [numberOfUsers, setNumberOfUsers] = useState(0);

  useEffect(() => {
    async function fetchNumberOfUsers() {
      const users = await getNumberOfUsers();
      setNumberOfUsers(users);
      console.log(numberOfUsers);
    }
    fetchNumberOfUsers();
  }, []);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        delayChildren: 0.3,
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1
    }
  };

  const steps = [
    {
      title: "01 | Informieren",
      description: "Verschaffe dir einen Überblick über die SKS-Prüfung in unserem kostenlosen Einsteiger-Guide und SKS-Wiki. Hier findest du alles, was du zum Start wissen musst.",
      image: View_Wiki,
      link: "/wiki/weg-zum-sks",
      linkText: "Unser Starter Wiki",
      color: "info",
      icon: BookOpenCheck,
    },
    {
      title: "02 | Themenbereiche erkunden",
      description: "Tauche ein in die fünf Hauptkategorien: Navigation, Schifffahrtsrecht, Wetterkunde und Seemannschaft. Unser Wiki unterstützt dich mit kompaktem Grundlagenwissen.",
      image: View_Topic,
      color: "primary",
      icon: BookOpen,
    },
    {
      title: "03 | In kleinen Schritten lernen",
      description: "Bleibe flexibel mit unseren 10-Minuten-Lerneinheiten. Die Flashcard-Methode hält dich fokussiert und macht das Lernen effizient.",
      image: View_Quiz,
      color: "secondary",
      icon: Clock,
    },
    {
      title: "04 | Gezielt wiederholen",
      description: "Unser intelligentes Spaced-Learning-System erkennt deinen Fortschritt und bringt wichtige Fragen zum optimalen Zeitpunkt zurück.",
      image: View_HomeScreen,
      color: "accent",
      icon: Brain,
    },
    {
      title: "05 | Prüfungsbereit werden",
      description: "Teste dein Wissen unter realen Bedingungen mit Original-Prüfungsbögen und sichere dir so den Erfolg.",
      image: View_Exam,
      color: "success",
      icon: GraduationCap,
    }
  ];

  return (
    <LayoutNoLogin>
      <Helmet>
        {/* Primary Meta Tags */}
        <title>Mein-SKS - SKS-Theoriefragen lernen | Intelligente Lern-App für die SKS-Prüfung</title>
        <meta name="title" content="Mein-SKS - SKS-Theoriefragen lernen | Intelligente Lern-App für die SKS-Prüfung" />
        <meta name="description" content="Bereite dich gezielt auf die SKS-Theorieprüfung vor. Intelligente Lern-App mit Spaced-Learning-System für alle SKS-Theoriefragen. Kostenlos testen - 19,99€ für 12 Monate." />
        <meta name="keywords" content="SKS Theorie, SKS Prüfung, SKS Führerschein, SKS Theoriefragen, SKS lernen, Sportküstenschifferschein, Navigation, Schifffahrtsrecht, Wetterkunde, Seemannschaft" />
        
        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://mein-sks.de/" />
        <meta property="og:title" content="Mein-SKS - SKS-Theoriefragen lernen | Intelligente Lern-App" />
        <meta property="og:description" content="Bereite dich gezielt auf die SKS-Theorieprüfung vor. Intelligente Lern-App mit Spaced-Learning-System für alle SKS-Theoriefragen." />
        <meta property="og:image" content="https://mein-sks.de/app_icon.png" />
        <meta property="og:site_name" content="Mein-SKS" />
        <meta property="og:locale" content="de_DE" />
        
        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://mein-sks.de/" />
        <meta property="twitter:title" content="Mein-SKS - SKS-Theoriefragen lernen | Intelligente Lern-App" />
        <meta property="twitter:description" content="Bereite dich gezielt auf die SKS-Theorieprüfung vor. Intelligente Lern-App mit Spaced-Learning-System für alle SKS-Theoriefragen." />
        <meta property="twitter:image" content="https://mein-sks.de/app_icon.png" />
        
        {/* Additional SEO */}
        <meta name="robots" content="index, follow" />
        <meta name="language" content="German" />
        <meta name="author" content="Mein-SKS" />
        <link rel="canonical" href="https://mein-sks.de/" />
        
        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebApplication",
            "name": "Mein-SKS",
            "description": "Intelligente Lern-App für SKS-Theoriefragen mit Spaced-Learning-System",
            "url": "https://mein-sks.de",
            "applicationCategory": "EducationalApplication",
            "operatingSystem": "Web Browser",
            "offers": {
              "@type": "Offer",
              "price": "19.99",
              "priceCurrency": "EUR",
              "availability": "https://schema.org/InStock"
            },
            "creator": {
              "@type": "Organization",
              "name": "Mein-SKS"
            }
          })}
        </script>
      </Helmet>
      <div className="flex flex-col min-h-screen  overflow-hidden">
        <main className="flex-1">
          {/* Hero Section - SEO optimiert */}
          <section className="hero min-h-[70vh] rounded-3xl relative" style={{ backgroundImage: `url(${heroImage})` }}>
            <div className="hero-overlay rounded-3xl bg-gradient-to-b from-gray-900/30 to-gray-900/60"></div>
            <div className="hero-content text-center text-neutral-content">
              <div className="max-w-2xl">
                <h1 className="mb-6 text-4xl md:text-5xl lg:text-6xl font-bold leading-tight">
                  SKS-Theoriefragen lernen mit der intelligenten Lern-App
                </h1>
                <p className="mb-8 text-lg md:text-xl lg:text-2xl font-light">
                  Bereite dich gezielt auf die SKS-Theorieprüfung vor. Mit unserem Spaced-Learning-System bestehst du die SKS-Theorie im Handumdrehen.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link to="/register">
                    <button className="btn btn-success btn-lg gap-2 min-w-[200px]">
                      SKS-Theorie kostenlos testen
                      <ChevronRight className="w-5 h-5" />
                    </button>
                  </Link>
                  <Link to="/login">
                    <button className="btn btn-lg btn-ghost text-white min-w-[200px] backdrop-blur-sm bg-base-100/10">
                      Zum Login
                    </button>
                  </Link>
                </div>
              </div>
            </div>
          </section>

          {/* Demo Quiz Section - SEO optimiert */}
          <section className="py-16">
            <div className="container mx-auto px-4">
              <motion.div
                className="text-center max-w-3xl mx-auto mb-8"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <h2 className="text-3xl font-bold mb-4">
                  SKS-Theoriefragen kostenlos testen
                </h2>
                <p className="text-lg text-base-content/70">
                  Probiere direkt aus, wie du mit unserer SKS-Lern-App die Theorieprüfung meisterst
                </p>
              </motion.div>

              <div className="grid md:grid-cols-2 gap-12 items-start max-w-6xl mx-auto">
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                >
                  <DemoQuiz />
                </motion.div>

                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  className="space-y-6"
                >
                  <div className="card bg-base-100 shadow-lg">
                    <div className="card-body">
                      <h3 className="card-title text-xl mb-4">Überzeuge Dich selbst von der SKS-Lern-App</h3>
                      <ul className="space-y-4">
                        <li className="flex items-start gap-3">
                          <CheckCircle className="w-5 h-5 text-success mt-1 shrink-0" />
                          <span>150 SKS-Theoriefragen zum Testen - keine Zahlungsinformationen erforderlich</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <CheckCircle className="w-5 h-5 text-success mt-1 shrink-0" />
                          <span>Voller Zugriff auf alle SKS-Funktionen</span>
                        </li>
                        <li className="flex items-start gap-3">
                          <CheckCircle className="w-5 h-5 text-success mt-1 shrink-0" />
                          <span>Anschließend 19,99 € für 12 Monate SKS-Vollzugriff</span>
                        </li>
                      </ul>

                      <div className="divider my-6"></div>

                      <div className="space-y-4">
                        <div className="flex items-start gap-3">
                          <BrainCircuit className="w-5 h-5 text-primary mt-1 shrink-0" />
                          <div>
                            <h4 className="font-medium">Intelligentes Spaced Learning für SKS-Theorie</h4>
                            <p className="text-sm text-base-content/70">Optimale Wiederholungsintervalle für deinen SKS-Lernerfolg</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <BarChart3 className="w-5 h-5 text-primary mt-1 shrink-0" />
                          <div>
                            <h4 className="font-medium">SKS-Fortschrittsverfolgung</h4>
                            <p className="text-sm text-base-content/70">Behalte den Überblick über deinen SKS-Lernfortschritt</p>
                          </div>
                        </div>
                        <div className="flex items-start gap-3">
                          <Target className="w-5 h-5 text-primary mt-1 shrink-0" />
                          <div>
                            <h4 className="font-medium">SKS-Prüfungsvorbereitung</h4>
                            <p className="text-sm text-base-content/70">Übe mit Original-SKS-Prüfungsfragen</p>
                          </div>
                        </div>
                      </div>

                      <div className="card-actions justify-end mt-6">
                        <Link to="/register">
                          <button className="btn btn-primary">
                            Jetzt für SKS-Theorie registrieren
                          </button>
                        </Link>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>
          </section>

          <div className="container mx-auto px-4">
            <hr className="border-base-content/10 my-8" />
          </div>

          {/* Learning Steps Section */}
          <LearningSteps />

          <div className="container mx-auto px-4">
            <hr className="border-base-content/10 my-8" />
          </div>

          {/* Benefits & Limitations - SEO optimiert */}
          <section className="py-8">
            <div className="container mx-auto px-4 max-w-6xl">
              <div className="grid md:grid-cols-2 gap-12">
                {/* Benefits */}
                <motion.div
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                >
                  <div className="card bg-base-200 shadow-lg h-full hover:shadow-xl transition-all">
                    <div className="card-body">
                      <h3 className="text-2xl font-bold mb-8 flex items-center gap-3">
                        <CheckCircle className="w-6 h-6 text-success" />
                        Warum Mein-SKS für deine SKS-Theorieprüfung?
                      </h3>
                      <ul className="space-y-6">
                        <li className="flex items-start gap-3">
                          <CheckCircle className="w-5 h-5 text-success mt-1" />
                          <div>
                            <strong className="block text-lg mb-1">Spezialisiert auf SKS-Theorieprüfung</strong>
                            <p className="text-base-content/70">Fokussierte Vorbereitung auf die schriftlichen SKS-Theoriefragen des Sportküstenschifferscheins</p>
                          </div>
                        </li>
                        <li className="flex items-start gap-3">
                          <CheckCircle className="w-5 h-5 text-success mt-1" />
                          <div>
                            <strong className="block text-lg mb-1">Umfassendes SKS-Theoriewissen</strong>
                            <p className="text-base-content/70">Alle relevanten Themengebiete der SKS-Theorieprüfung: Navigation, Schifffahrtsrecht, Wetterkunde und Seemannschaft</p>
                          </div>
                        </li>
                        <li className="flex items-start gap-3">
                          <CheckCircle className="w-5 h-5 text-success mt-1" />
                          <div>
                            <strong className="block text-lg mb-1">Prüfungsrelevante SKS-Lernmethode</strong>
                            <p className="text-base-content/70">Spaced-Learning-System optimiert für die SKS-Theorieprüfung und nachhaltigen Lernerfolg</p>
                          </div>
                        </li>
                        <li className="flex items-start gap-3">
                          <CheckCircle className="w-5 h-5 text-success mt-1" />
                          <div>
                            <strong className="block text-lg mb-1">Flexibles SKS-Lernen überall</strong>
                            <p className="text-base-content/70">Lerne die SKS-Theorie mobil auf Smartphone, Tablet und Desktop - wann und wo du willst</p>
                          </div>
                        </li>
                      </ul>
                    </div>
                  </div>
                </motion.div>

                {/* Limitations */}
                <motion.div
                  initial={{ opacity: 0, x: 20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                >
                  <div className="card bg-base-200 shadow-lg hover:shadow-xl transition-all">
                    <div className="card-body">
                      <h3 className="text-2xl font-bold mb-4 flex items-center gap-3">
                        <AlertTriangle className="w-6 h-6 text-warning" />
                        Wichtiger Hinweis zur SKS-Prüfung
                      </h3>
                      <div className="rounded-lg">
                        <p className="text-lg mb-2">
                          Diese App deckt die <strong>theoretischen SKS-Prüfungsfragen</strong> ab.
                        </p>
                        <p className="text-base-content/70">
                          Für die SKS-Kartenaufgaben findest du Tipps und Anleitungen in unserem <Link to="/wiki/kartenaufgaben" className="link link-primary">SKS-Wiki für Kartenaufgaben</Link>.
                        </p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </div>
            </div>
          </section>

          {/* FAQ Section */}
          <FAQSection />

          {/* CTA Section - SEO optimiert */}
          <section className="card shadow-lg border border-base-300 mx-4 mb-4 bg-success/10 rounded-3xl">
            <div className="container mx-auto px-4 py-12">
              <motion.div
                className="text-center mx-auto"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <div className="max-w-xl mx-auto">
                  <h2 className="text-3xl font-bold mb-2">
                    Starte jetzt deine SKS-Theorieprüfung Vorbereitung
                  </h2>
                  <p className="text-lg text-base-content/70 mb-8">
                    Deine intelligente SKS-Lern-App für die Sportküstenschifferschein-Theorie
                  </p>

                  <div className="bg-base-200 rounded-xl p-6">
                    <div className="flex justify-between items-start gap-4 mb-6">
                      <div>
                        <div className="text-3xl font-bold">19,99 €</div>
                        <div className="text-base-content/70">für 12 Monate SKS-Zugang</div>
                      </div>
                      <div className="badge badge-success h-fit">Spare über 20€ vs. Bücher</div>
                    </div>
                    
                    <div className="space-y-3 mb-6">
                      <div className="flex items-center gap-3">
                        <CheckCircle className="w-5 h-5 text-success shrink-0" />
                        <span>Unbegrenzter Zugriff auf alle SKS-Theoriefragen</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <CheckCircle className="w-5 h-5 text-success shrink-0" />
                        <span>Intelligentes SKS-Lernsystem mit Spaced Learning</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <CheckCircle className="w-5 h-5 text-success shrink-0" />
                        <span>Günstiger als klassische SKS-Lernmaterialien</span>
                      </div>
                      <div className="flex items-center gap-3">
                        <CheckCircle className="w-5 h-5 text-success shrink-0" />
                        <span>Keine automatische Verlängerung</span>
                      </div>
                    </div>

                    <Link to="/register" className="block">
                      <button className="btn btn-success btn-lg w-full gap-2 text-success-content">
                        SKS-Theorie kostenlos testen
                        <ChevronRight className="w-5 h-5" />
                      </button>
                    </Link>

                    <p className="text-center text-sm text-base-content/60 mt-4">
                      Starte mit 150 kostenlosen SKS-Theoriefragen
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>
          </section>
        </main>
      </div>
    </LayoutNoLogin>
  );
};

export default LandingPage;